import { Affix, Button, Card, Center, Loader, Transition } from '@mantine/core';
import { useWindowScroll } from '@mantine/hooks';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconArrowNarrowUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { Permission } from '../../../../models/core/departments.type';
import { EntityType } from '../../../../models/core/entities.type';
import entitiesService from '../../../../services/core/entities.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: EntityType | null;
};

function EntityView() {
  const navigate = useNavigate();
  const modals = useModals();
  const { idEntidade } = useParams();
  const [scroll, scrollTo] = useWindowScroll();

  const [result, setResult] = useState<Result>({
    loading: true,
    data: null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: EntityType | null = null;
      try {
        data = await entitiesService.select({ idEntidade: Number(idEntidade) });
      } catch (error: any) {
        showNotification({
          title: 'Entidade - Visualizar',
          message: error?.isBusinessException ? error.description : `Não foi possível carregar a entidade.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idEntidade, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await entitiesService.delete({ idEntidade: Number(idEntidade) });
      navigate('/entities');

      showNotification({
        title: 'Entidade - Visualizar',
        message: `Entidade excluída com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Entidade - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir a entidade.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir essa entidade?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Entity}
        title="Entidade - Visualizar"
        description="Visualize os detalhes de uma entidade."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            loading={deleting}
            data-permission={Permission.EntityDelete}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/entities/${result?.data?.idEntidade}/edit`, {
                state: {
                  entity: result.data,
                },
              });
            }}
            disabled={deleting || result.loading}
            data-permission={Permission.EntityEdit}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
      <Affix position={{ bottom: 20, left: '50%' }}>
        <Transition transition="slide-up" mounted={scroll.y > 0}>
          {(transitionStyles) => (
            <Button
              leftSection={<IconArrowNarrowUp size={18} />}
              style={transitionStyles}
              color="secondary"
              onClick={() => scrollTo({ y: 0 })}
            >
              Ir ao topo
            </Button>
          )}
        </Transition>
      </Affix>
    </Card>
  );
}

export default EntityView;
